import { initNewRelic, type NewrelicConfig } from '@ptml/observability';
import { getApiBaseUrl, isProductionDomain } from '@ptml/root-config';

declare global {
    // Disabled 'cause the right way of doing this is overriding the interface,
    // can't be changed to a type
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Window {
        NREUM: unknown;
        newrelic?: {
            setUserId: (userId: string) => void;
            setCustomAttribute: (key: string, value: string | number | boolean) => void;
            addPageAction: (name: string, attributes?: Record<string, any>) => void;
            noticeError: (error: Error, customAttributes?: Record<string, any>) => void;
            setCurrentRouteName: (routeName: string) => void;
            interaction: () => {
                setName: (name: string) => void;
                save: () => void;
                ignore: () => void;
                setAttribute: (key: string, value: string | number | boolean) => void;
                addToTrace: (attributes: Record<string, any>) => void;
            };
        };
    }

    let newrelic: Window['newrelic'];
}

// These keys are not secrets, they are public.
const newRelicConfig = {
    TEST: {
        accountId: '4070317',
        trustKey: '739791',
        agentId: '601617540',
        licenseKey: 'NRJS-53d13d34657eadd0c31',
        applicationId: '601617540',
    },
    PROD: {
        accountId: '1806149',
        trustKey: '739791',
        agentId: '601618124',
        licenseKey: 'NRBR-dde58259132154a9fb5',
        applicationId: '601618124',
    },
};

/**
 * Initialises New Relic agent with environment-specific configuration.
 * @param userData - User data object containing name, roles, and company info
 * @returns Promise that resolves when New Relic is initialised
 * @example
 * ```typescript
 * await loadNewRelicAgent(userData);
 * await loadNewRelicAgent(); // Without user data
 * ```
 */
export const loadNewRelicAgent = async (userId: string | null): Promise<void> => {
    try {
        const { PROD, TEST } = newRelicConfig;
        let config: NewrelicConfig = TEST;

        if (isProductionDomain()) {
            config = PROD;
        }

        // Cross-origin API calls only receive trace headers when the origin is allow-listed
        config = { ...config, allowedOrigins: [getApiBaseUrl()] };

        // Set userId if is provided
        if (userId) {
            config = { ...config, userId };
        }

        initNewRelic(config);
    } catch (error) {
        console.warn('Failed to load New Relic agent:', error);
    }
};
